import { utils } from "@defuse-protocol/internal-utils"

export const NEARCOM_ADDRESS_PREFIX = "nearcom:"

export type ParsedNearcomAddress = {
  readonly displayAddress: string
  readonly bareIdentifier: string
}

export function parseNearcomAddress(
  value: string
): ParsedNearcomAddress | null {
  const trimmed = value.trim()
  if (!trimmed.startsWith(NEARCOM_ADDRESS_PREFIX)) return null

  const bareIdentifier = trimmed
    .slice(NEARCOM_ADDRESS_PREFIX.length)
    .toLowerCase()

  if (!isValidNearcomIdentifier(bareIdentifier)) return null

  return {
    displayAddress: `${NEARCOM_ADDRESS_PREFIX}${bareIdentifier}`,
    bareIdentifier,
  }
}

/**
 * Returns the canonical form of a structurally valid legacy bare identifier.
 *
 * This does not establish that the recipient exists or is safe to send to;
 * callers must run the normal recipient validation against displayAddress.
 */
export function parseLegacyNearcomAddress(
  value: string
): ParsedNearcomAddress | null {
  const trimmed = value.trim()
  if (parseNearcomAddress(trimmed) != null) return null
  return parseNearcomAddress(`${NEARCOM_ADDRESS_PREFIX}${trimmed}`)
}

/**
 * Canonicalizes an address read from a persisted/display-only surface.
 *
 * Withdraw form input intentionally uses parseNearcomAddress directly so a
 * bare identifier cannot silently become an internal recipient. Contact and
 * history records predate that invariant, so those read paths may opt into
 * canonicalizing a legacy bare identifier.
 */
export function normalizeNearcomAddress(
  value: string,
  options?: { allowBare?: boolean }
): string {
  const trimmed = value.trim()
  const parsed = parseNearcomAddress(trimmed)
  if (parsed != null) return parsed.displayAddress
  if (!options?.allowBare) return trimmed

  return (
    parseNearcomAddress(`${NEARCOM_ADDRESS_PREFIX}${trimmed}`)
      ?.displayAddress ?? trimmed
  )
}

export function formatNearcomAddress(bareIdentifier: string): string {
  const parsed = parseNearcomAddress(
    `${NEARCOM_ADDRESS_PREFIX}${bareIdentifier}`
  )
  if (parsed == null) {
    throw new Error("Invalid near.com internal identifier")
  }
  return parsed.displayAddress
}

function isValidNearcomIdentifier(value: string): boolean {
  return utils.validateNearAddress(value) || isEvmAddress(value)
}

function isEvmAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address)
}

export function isMatchingNearcomRecipient(
  displayAddress: string,
  bareIdentifier: string
): boolean {
  const parsed = parseNearcomAddress(displayAddress)
  return parsed?.bareIdentifier === bareIdentifier
}
